import React, { useEffect, useMemo, useState } from "react";

/* ===================== helpers ===================== */

function safeStr(value) {
  return value == null ? "" : String(value);
}

function toNumber(value) {
  if (value == null || value === "" || safeStr(value).toLowerCase() === "dome") {
    return null;
  }

  const n = Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function normTeamAbbr(value) {
  const team = safeStr(value).trim().toUpperCase();

  // Normalize a few common NFL abbreviation variants so the
  // weather feed and Vegas feed still match if they use different codes.
  const aliases = {
    JAC: "JAX",
    WSH: "WAS",
    OAK: "LV",
    SD: "LAC",
    STL: "LAR",
  };

  return aliases[team] || team;
}

function parseGameTeams(value) {
  const raw = safeStr(value).trim();
  if (!raw) return { away: "", home: "" };

  const match = raw.match(/^\s*([A-Za-z]{2,4})\s*(?:@|at|vs\.?|v\.?|-)\s*([A-Za-z]{2,4})\s*$/i);
  if (!match) return { away: "", home: "" };

  return {
    away: normTeamAbbr(match[1]),
    home: normTeamAbbr(match[2]),
  };
}

function weatherGameKey(row) {
  let away = normTeamAbbr(row?.["Away Team"] ?? row?.away ?? row?.away_team);
  let home = normTeamAbbr(row?.["Home Team"] ?? row?.home ?? row?.home_team);

  // Fallback to the "Game" field if explicit team columns are missing.
  if (!away || !home) {
    const parsed = parseGameTeams(row?.Game ?? row?.game);
    away = away || parsed.away;
    home = home || parsed.home;
  }

  return away && home ? `${away}@${home}` : "";
}

async function fetchFirstJson(urls, label) {
  let lastError = null;

  for (const url of urls) {
    try {
      // Add a cache buster because these files are regenerated frequently.
      const separator = url.includes("?") ? "&" : "?";
      const response = await fetch(`${url}${separator}t=${Date.now()}`, {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`${url} returned ${response.status}`);
      }

      const json = await response.json();
      const rows = coerceToArray(json);

      if (!rows.length) {
        throw new Error(`${url} loaded but contained 0 rows`);
      }

      console.log(`${label} source:`, url);
      console.log(`${label} rows loaded:`, rows.length);
      return rows;
    } catch (error) {
      lastError = error;
      console.warn(`Failed ${label} source ${url}:`, error);
    }
  }

  throw lastError || new Error(`No ${label} source could be loaded`);
}

function coerceToArray(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.games)) return json.games;
  if (Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.rows)) return json.rows;
  if (typeof json === "object") return Object.values(json);
  return [];
}

function parseDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseGameDate(value) {
  const raw = safeStr(value).trim();
  if (!raw) return null;

  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalized);

  return Number.isNaN(d.getTime()) ? null : d;
}

function formatVegasGameTime(value) {
  const d = parseGameDate(value);
  if (!d) return "—";

  const hour = d.getHours();
  const minute = d.getMinutes();
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;

  return minute === 0
    ? `${hour12} ${suffix}`
    : `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function formatKickoff(value) {
  const d = parseDate(value);
  if (!d) return "—";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}


function formatHour(value) {
  const d = parseDate(value);
  if (!d) return "—";

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function formatWeatherLocalHour(row) {
  // Prefer the generator's explicit stadium-local display string.
  // Example: "2026-09-09 05:00 PM PDT"
  const display = safeStr(row?.["Forecast Time Display"]).trim();

  if (display) {
    const match = display.match(/\b(\d{1,2}):(\d{2})\s*(AM|PM)\b/i);
    if (match) {
      return `${Number(match[1])}:${match[2]} ${match[3].toUpperCase()}`;
    }
  }

  // Fallback: preserve the wall-clock portion of an offset ISO timestamp
  // instead of converting it into the viewer/browser timezone.
  const raw = safeStr(row?.["Forecast Time"]).trim();
  const isoMatch = raw.match(/T(\d{2}):(\d{2})/);

  if (isoMatch) {
    const hour24 = Number(isoMatch[1]);
    const minute = isoMatch[2];
    const suffix = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${minute} ${suffix}`;
  }

  return formatHour(raw);
}

function conditionToIcon(condition) {
  const c = safeStr(condition).toLowerCase();

  if (c.includes("dome")) return "🏟️";
  if (c.includes("snow") || c.includes("sleet")) return "❄️";
  if (c.includes("thunder") || c.includes("storm")) return "⛈️";
  if (c.includes("rain") || c.includes("drizzle") || c.includes("shower")) return "🌧️";
  if (c.includes("fog") || c.includes("mist")) return "🌫️";
  if (c.includes("clear") || c.includes("sun")) return "☀️";
  if (c.includes("cloud") || c.includes("overcast")) return "☁️";

  return "🌤️";
}

function roofLabel(value) {
  const roof = safeStr(value).toLowerCase();

  if (roof === "fixed_dome") return "Dome";
  if (roof === "retractable") return "Retractable Roof";
  if (roof === "covered_open_sides") return "Covered / Open Sides";
  if (roof === "outdoor") return "Outdoor";

  return safeStr(value).replaceAll("_", " ") || "Unknown Roof";
}

function isFixedDome(row) {
  return safeStr(row?.["Roof Type"]).toLowerCase() === "fixed_dome";
}

function isWeatherProtected(row) {
  const roof = safeStr(row?.["Roof Type"]).toLowerCase();
  return roof === "fixed_dome";
}

/* ===================== NFL impact model ===================== */
/*
  Scores are descriptive flags, not projection adjustments.

  Weather Severity:
  0 = no meaningful concern
  100 = extreme playing conditions

  Passing Impact:
  0 = neutral
  100 = most likely to suppress downfield passing efficiency/volume

  Kicking Impact:
  0 = neutral
  100 = most likely to hurt field-goal accuracy/range
*/

function calculateNflImpact(rows) {
  if (!rows.length || rows.every(isFixedDome)) {
    return {
      severity: 0,
      passing: 0,
      kicking: 0,
      label: "Controlled",
    };
  }

  const available = rows.filter((r) => r["Forecast Available"] !== false);
  const source = available.length ? available : rows;

  const winds = source.map((r) => toNumber(r["Wind Speed (mph)"])).filter(Number.isFinite);
  const gusts = source.map((r) => toNumber(r["Wind Gust (mph)"])).filter(Number.isFinite);
  const temps = source.map((r) => toNumber(r["Temperature (°F)"])).filter(Number.isFinite);
  const precip = source
    .map((r) => toNumber(r["Precipitation Probability (%)"]))
    .filter(Number.isFinite);
  const rain = source.map((r) => toNumber(r["Rain Next Hour (mm)"])).filter(Number.isFinite);
  const snow = source.map((r) => toNumber(r["Snow Next Hour (mm)"])).filter(Number.isFinite);
  const visibility = source.map((r) => toNumber(r["Visibility (m)"])).filter(Number.isFinite);

  const maxWind = winds.length ? Math.max(...winds) : 0;
  const maxGust = gusts.length ? Math.max(...gusts) : maxWind;
  const minTemp = temps.length ? Math.min(...temps) : 65;
  const maxPrecip = precip.length ? Math.max(...precip) : 0;
  const maxRain = rain.length ? Math.max(...rain) : 0;
  const maxSnow = snow.length ? Math.max(...snow) : 0;
  const minVisibility = visibility.length ? Math.min(...visibility) : 16000;

  const conditionText = source.map((r) => safeStr(r.Conditions)).join(" ").toLowerCase();
  const hasRain = /rain|drizzle|shower/.test(conditionText) || maxRain > 0;
  const hasSnow = /snow|sleet|freezing/.test(conditionText) || maxSnow > 0;
  const hasStorm = /storm|thunder/.test(conditionText);

  let severity = 0;
  let passing = 0;
  let kicking = 0;

  // Sustained wind is the most important football weather variable.
  if (maxWind >= 25) {
    severity += 45;
    passing += 55;
    kicking += 60;
  } else if (maxWind >= 20) {
    severity += 35;
    passing += 45;
    kicking += 50;
  } else if (maxWind >= 15) {
    severity += 23;
    passing += 30;
    kicking += 35;
  } else if (maxWind >= 10) {
    severity += 10;
    passing += 12;
    kicking += 15;
  }

  // Gusts matter independently, especially for kicks and deep throws.
  if (maxGust >= 35) {
    severity += 25;
    passing += 25;
    kicking += 30;
  } else if (maxGust >= 25) {
    severity += 15;
    passing += 15;
    kicking += 20;
  } else if (maxGust >= 20) {
    severity += 8;
    passing += 8;
    kicking += 10;
  }

  // Precipitation primarily affects ball security, footing and efficiency.
  if (hasStorm) {
    severity += 25;
    passing += 15;
    kicking += 12;
  } else if (hasSnow) {
    severity += maxSnow >= 2 ? 30 : 20;
    passing += maxSnow >= 2 ? 25 : 15;
    kicking += maxSnow >= 2 ? 25 : 15;
  } else if (hasRain || maxPrecip >= 60) {
    severity += maxRain >= 2 ? 22 : 14;
    passing += maxRain >= 2 ? 18 : 10;
    kicking += maxRain >= 2 ? 15 : 8;
  }

  // Cold alone is generally less important than wind/precipitation.
  if (minTemp <= 10) {
    severity += 18;
    passing += 10;
    kicking += 12;
  } else if (minTemp <= 20) {
    severity += 12;
    passing += 7;
    kicking += 8;
  } else if (minTemp <= 32) {
    severity += 6;
    passing += 3;
    kicking += 4;
  }

  if (minVisibility < 3000) {
    severity += 12;
    passing += 8;
    kicking += 8;
  }

  severity = Math.min(100, Math.round(severity));
  passing = Math.min(100, Math.round(passing));
  kicking = Math.min(100, Math.round(kicking));

  let label = "Low";
  if (severity >= 70) label = "Extreme";
  else if (severity >= 45) label = "High";
  else if (severity >= 25) label = "Moderate";

  return { severity, passing, kicking, label };
}

/* ===================== badge helpers ===================== */

function impactBadge(label, score) {
  if (score >= 70) {
    return {
      text: `${label} ${score}`,
      cls: "border-red-500/35 bg-red-500/15 text-red-200",
    };
  }

  if (score >= 45) {
    return {
      text: `${label} ${score}`,
      cls: "border-orange-500/35 bg-orange-500/15 text-orange-200",
    };
  }

  if (score >= 25) {
    return {
      text: `${label} ${score}`,
      cls: "border-amber-500/30 bg-amber-500/15 text-amber-100",
    };
  }

  return {
    text: `${label} ${score}`,
    cls: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
  };
}

/* ===================== logos ===================== */

function TeamLogo({ abbr, size = 24 }) {
  const team = normTeamAbbr(abbr);
  const [srcIdx, setSrcIdx] = useState(0);

  const candidates = useMemo(
    () => [
      `/logos/nfl/${team}.png`,
      `/logos/nfl/${team}.svg`,
      `/img/nfl/logos/${team}.png`,
      `/img/nfl/${team}.png`,
    ],
    [team]
  );

  return (
    <div
      className="flex items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5"
      style={{ width: size + 12, height: size + 12 }}
      title={team}
    >
      <img
        src={candidates[srcIdx]}
        alt={team}
        width={size}
        height={size}
        className="object-contain"
        onError={() => {
          if (srcIdx < candidates.length - 1) setSrcIdx((x) => x + 1);
        }}
      />
    </div>
  );
}

/* ===================== wind arrow ===================== */

function WindArrow({ degrees = 0, mph = 0, size = 58, subtle = false }) {
  const deg = Number.isFinite(Number(degrees)) ? Number(degrees) : 0;
  const speed = Number.isFinite(Number(mph)) ? Number(mph) : 0;

  const pulseClass =
    speed >= 20
      ? "animate-[pulse_1.2s_ease-in-out_infinite]"
      : speed >= 12
      ? "animate-[pulse_1.8s_ease-in-out_infinite]"
      : "";

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
      title={`${Math.round(speed)} mph`}
    >
      <div className="absolute inset-0 rounded-full border border-white/10 bg-white/[0.04]" />
      <div className="absolute inset-[6px] rounded-full border border-white/8" />

      <div
        className={`${pulseClass} transition-transform duration-500`}
        style={{ transform: `rotate(${deg}deg)` }}
      >
        <svg
          width={Math.round(size * (subtle ? 0.53 : 0.62))}
          height={Math.round(size * (subtle ? 0.53 : 0.62))}
          viewBox="0 0 64 64"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 32H42"
            stroke="rgba(255,255,255,0.92)"
            strokeWidth={subtle ? "5" : "6"}
            strokeLinecap="round"
          />
          <path
            d="M42 32L30 21"
            stroke="rgba(255,255,255,0.92)"
            strokeWidth={subtle ? "5" : "6"}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42 32L30 43"
            stroke="rgba(255,255,255,0.92)"
            strokeWidth={subtle ? "5" : "6"}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

/* ===================== component ===================== */

export default function NflWeather() {
  const [weatherRows, setWeatherRows] = useState([]);
  const [vegasRows, setVegasRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState("kickoff");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);

      const vegasUrls = [
        "/data/nfl/classic/latest/vegas.json",
      ];

      // Try the canonical frontend filename first, then the two filenames
      // your weather export has used. The first valid non-empty JSON wins.
      const weatherUrls = [
        "/data/nfl/latest/weather_hourly.json",
        "/data/nfl/latest/weather_forecast_hourly.json",
        "/data/nfl/latest/weather_game_time.json",
      ];

      let weatherData = [];
      let vegasData = [];

      try {
        vegasData = await fetchFirstJson(vegasUrls, "NFL Vegas");
      } catch (error) {
        console.error("Failed to load NFL Vegas data:", error);
      }

      try {
        weatherData = await fetchFirstJson(weatherUrls, "NFL weather");

        // Helpful diagnostics: proves what games the browser actually loaded.
        const loadedWeatherKeys = [
          ...new Set(weatherData.map(weatherGameKey).filter(Boolean)),
        ];

        console.log("NFL weather game keys:", loadedWeatherKeys);
      } catch (error) {
        console.error("Failed to load NFL weather data:", error);
      }

      if (mounted) {
        setVegasRows(vegasData);
        setWeatherRows(weatherData);
        setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const gameCards = useMemo(() => {
    /*
      vegas.json controls the game list and betting information.

      Expected fields:
      team, opponent, field, gameDate, moneyline,
      spread, over_under, team_total

      "field" is the home team abbreviation.
    */
    const weatherByGame = new Map();

    for (const row of weatherRows) {
      const key = weatherGameKey(row);
      if (!key) continue;

      if (!weatherByGame.has(key)) weatherByGame.set(key, []);
      weatherByGame.get(key).push(row);
    }

    for (const rows of weatherByGame.values()) {
      rows.sort(
        (a, b) =>
          (toNumber(a["Game Hour"]) ?? 0) - (toNumber(b["Game Hour"]) ?? 0)
      );
    }

    const vegasGames = new Map();

    for (const row of vegasRows) {
      const team = normTeamAbbr(row.team);
      const opponent = normTeamAbbr(row.opponent);
      const home = normTeamAbbr(row.field);

      if (!team || !opponent || !home) continue;

      const away = team === home ? opponent : team;
      const key = `${away}@${home}`;

      if (!vegasGames.has(key)) {
        vegasGames.set(key, {
          key,
          away,
          home,
          gameDate: safeStr(row.gameDate),
          ou: row.over_under ?? "",
          awayTotal: null,
          homeTotal: null,
          awaySpread: null,
          homeSpread: null,
          awayMoneyline: null,
          homeMoneyline: null,
        });
      }

      const game = vegasGames.get(key);

      if (!game.gameDate && row.gameDate) {
        game.gameDate = safeStr(row.gameDate);
      }

      if ((game.ou === "" || game.ou == null) && row.over_under != null) {
        game.ou = row.over_under;
      }

      const total = toNumber(row.team_total);
      const spread = toNumber(row.spread);
      const moneyline = toNumber(row.moneyline);

      if (team === away) {
        if (total != null) game.awayTotal = total;
        if (spread != null) game.awaySpread = spread;
        if (moneyline != null) game.awayMoneyline = moneyline;
      }

      if (team === home) {
        if (total != null) game.homeTotal = total;
        if (spread != null) game.homeSpread = spread;
        if (moneyline != null) game.homeMoneyline = moneyline;
      }
    }

    return Array.from(vegasGames.values()).map((game) => {
      const rows = weatherByGame.get(game.key) || [];
      const first = rows[0] || null;

      if (!rows.length) {
        console.warn(
          `No weather match for ${game.key}. Available weather keys:`,
          Array.from(weatherByGame.keys())
        );
      }

      return {
        ...game,
        rows,
        first,
        kickoff: safeStr(first?.Kickoff) || game.gameDate,
        stadium: safeStr(first?.Stadium),
        roofType: safeStr(first?.["Roof Type"]),
        impact: calculateNflImpact(rows),
      };
    });
  }, [weatherRows, vegasRows]);

  const sortedCards = useMemo(() => {
    const cards = [...gameCards];

    cards.sort((a, b) => {
      if (sortMode === "severity") {
        return b.impact.severity - a.impact.severity;
      }

      if (sortMode === "passing") {
        return b.impact.passing - a.impact.passing;
      }

      if (sortMode === "kicking") {
        return b.impact.kicking - a.impact.kicking;
      }

      const aKickoff = parseGameDate(a.gameDate)?.getTime();
      const bKickoff = parseGameDate(b.gameDate)?.getTime();

      if (aKickoff != null && bKickoff != null) return aKickoff - bKickoff;
      return safeStr(a.gameDate).localeCompare(safeStr(b.gameDate));
    });

    return cards;
  }, [gameCards, sortMode]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-zinc-950 text-white">
        <div className="text-white/70">Loading NFL Weather…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">NFL Weather</h1>
            <p className="mt-1 text-white/60">
              Game-window forecasts • {sortedCards.length} games
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ["kickoff", "Kickoff"],
              ["severity", "Weather Impact"],
              ["passing", "Pass Impact"],
              ["kicking", "Kick Impact"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setSortMode(value)}
                className={`rounded-xl border px-3 py-2 text-sm hover:bg-white/10 ${
                  sortMode === value
                    ? "border-white/20 bg-white/10"
                    : "border-white/10 bg-white/5"
                }`}
              >
                Sort: {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4">
          {sortedCards.map((game) => {
            const { first, impact } = game;
            const dome = isWeatherProtected(first);

            const severityBadge = impactBadge("Weather", impact.severity);
            const passingBadge = impactBadge("Pass", impact.passing);
            const kickingBadge = impactBadge("Kick", impact.kicking);

            const temp = toNumber(first?.["Temperature (°F)"]);
            const feelsLike = toNumber(first?.["Feels Like (°F)"]);
            const wind = toNumber(first?.["Wind Speed (mph)"]);
            const gust = toNumber(first?.["Wind Gust (mph)"]);
            const windDirection = safeStr(first?.["Wind Direction"]);
            const windDegrees = toNumber(first?.["Wind Degrees"]);
            const precip = toNumber(first?.["Precipitation Probability (%)"]);
            const conditions = dome
              ? "Controlled indoor conditions"
              : safeStr(first?.Conditions) || "Forecast unavailable";

            return (
              <div
                key={game.key}
                className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent"
              >
                <div className="flex flex-col gap-3 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <TeamLogo abbr={game.away} />
                      <span className="text-white/40">@</span>
                      <TeamLogo abbr={game.home} />
                    </div>

                    <div className="text-lg font-semibold">
                      {game.away} @ {game.home}
                    </div>

                    <span className={`rounded-full border px-2.5 py-1 text-xs ${severityBadge.cls}`}>
                      {severityBadge.text}
                    </span>
                    <span className={`rounded-full border px-2.5 py-1 text-xs ${passingBadge.cls}`}>
                      {passingBadge.text}
                    </span>
                    <span className={`rounded-full border px-2.5 py-1 text-xs ${kickingBadge.cls}`}>
                      {kickingBadge.text}
                    </span>

                    <span className="rounded-full border border-white/12 bg-white/5 px-2.5 py-1 text-xs text-white/75">
                      {roofLabel(game.roofType)}
                    </span>
                  </div>

                  <div className="text-right">
                    <div className="text-lg font-semibold text-white">
                      {formatVegasGameTime(game.gameDate)}
                    </div>
                    {game.gameDate ? (
                      <div className="text-xs text-white/50">
                        {formatKickoff(game.gameDate)}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 border-b border-white/10 px-5 py-3 text-xs">
                  {game.ou !== "" && game.ou != null ? (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/75">
                      O/U {game.ou}
                    </span>
                  ) : null}

                  {game.awayTotal != null ? (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/75">
                      {game.away} total {game.awayTotal}
                    </span>
                  ) : null}

                  {game.homeTotal != null ? (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/75">
                      {game.home} total {game.homeTotal}
                    </span>
                  ) : null}

                  {game.awaySpread != null ? (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/75">
                      {game.away} {game.awaySpread > 0 ? "+" : ""}
                      {game.awaySpread}
                    </span>
                  ) : null}

                  {game.homeSpread != null ? (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-white/75">
                      {game.home} {game.homeSpread > 0 ? "+" : ""}
                      {game.homeSpread}
                    </span>
                  ) : null}
                </div>

                <div className="grid gap-4 p-5 md:grid-cols-[280px_1fr]">
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-3xl">{conditionToIcon(conditions)}</div>
                      <div className="text-right text-xs text-white/60">{conditions}</div>
                    </div>

                    <div className="mt-4 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-5xl font-semibold">
                          {dome ? "Dome" : temp == null ? "—" : `${Math.round(temp)}°`}
                        </div>
                        {!dome && feelsLike != null ? (
                          <div className="mt-1 text-sm text-white/55">
                            Feels like {Math.round(feelsLike)}°
                          </div>
                        ) : null}
                      </div>

                      {!dome ? (
                        <WindArrow
                          degrees={windDegrees ?? 0}
                          mph={wind ?? 0}
                          size={62}
                        />
                      ) : null}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-white/55">Wind</div>
                        <div className="mt-1 font-medium">
                          {dome
                            ? "Controlled"
                            : wind == null
                            ? "—"
                            : `${Math.round(wind)} mph ${windDirection}`}
                        </div>
                        {!dome && gust != null ? (
                          <div className="mt-1 text-xs text-white/50">
                            Gusts {Math.round(gust)} mph
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-white/55">Precipitation</div>
                        <div className="mt-1 font-medium">
                          {dome ? "None" : precip == null ? "—" : `${Math.round(precip)}%`}
                        </div>
                        <div className="mt-1 text-xs text-white/50">
                          Game-hour probability
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.035] p-3">
                      <div className="text-xs font-medium text-white/75">
                        Football impact
                      </div>
                      <div className="mt-1 text-xs leading-5 text-white/50">
                        Wind drives pass and kick risk. Rain, snow, cold and poor
                        visibility increase the overall severity score.
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="text-sm font-medium">Game Window</div>
                      <div className="text-xs text-white/60">
                        Kickoff through Hour 3
                      </div>
                    </div>

                    {game.rows.length ? (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {game.rows.map((row) => {
                        const rowDome = isFixedDome(row);
                        const rowTemp = toNumber(row["Temperature (°F)"]);
                        const rowWind = toNumber(row["Wind Speed (mph)"]);
                        const rowGust = toNumber(row["Wind Gust (mph)"]);
                        const rowPrecip = toNumber(row["Precipitation Probability (%)"]);
                        const rowDegrees = toNumber(row["Wind Degrees"]);
                        const rowCondition = rowDome
                          ? "Dome"
                          : safeStr(row.Conditions) || "Unavailable";

                        return (
                          <div
                            key={`${game.key}_${safeStr(row["Game Hour"])}`}
                            className="rounded-xl border border-white/10 bg-white/5 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-white/60">
                                {toNumber(row["Game Hour"]) === 0
                                  ? "Kickoff"
                                  : `Hour ${toNumber(row["Game Hour"])}`}
                              </div>
                              <div className="text-lg">
                                {conditionToIcon(rowCondition)}
                              </div>
                            </div>

                            <div className="mt-1 text-[11px] text-white/45">
                              {formatWeatherLocalHour(row)}
                            </div>

                            <div className="mt-2 flex items-start justify-between gap-2">
                              <div className="text-lg font-semibold">
                                {rowDome
                                  ? "Dome"
                                  : rowTemp == null
                                  ? "—"
                                  : `${Math.round(rowTemp)}°`}
                              </div>

                              {!rowDome && rowWind != null ? (
                                <WindArrow
                                  degrees={rowDegrees ?? 0}
                                  mph={rowWind}
                                  size={38}
                                  subtle
                                />
                              ) : null}
                            </div>

                            <div className="mt-2 text-xs text-white/60">
                              {rowDome
                                ? "Controlled conditions"
                                : rowWind == null
                                ? "Wind unavailable"
                                : `${Math.round(rowWind)} mph${
                                    rowGust != null ? ` • G ${Math.round(rowGust)}` : ""
                                  }`}
                            </div>

                            {!rowDome ? (
                              <div className="mt-1 text-[11px] text-white/50">
                                Precip: {rowPrecip == null ? "—" : `${Math.round(rowPrecip)}%`}
                              </div>
                            ) : null}

                            <div className="mt-1 line-clamp-1 text-[11px] text-white/45">
                              {rowCondition}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    ) : (
                      <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-white/60">
                        No matching hourly weather rows were found for {game.away} @ {game.home}.
                      </div>
                    )}

                    <div className="mt-3 text-[11px] text-white/45">
                      {game.stadium || "Stadium unavailable"} • {roofLabel(game.roofType)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {!sortedCards.length ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/70">
              No NFL games found. Verify{" "}
              <span className="font-mono text-white/85">
                /data/nfl/classic/latest/vegas.json
              </span>
              .
            </div>
          ) : null}
        </div>

        <div className="mt-8 text-xs text-white/40">
          Logos expected at{" "}
          <span className="font-mono">/public/logos/nfl/TEAM.png</span>.
        </div>
      </div>
    </div>
  );
}
